import { useEffect, useMemo, useRef, useState } from "react";
import type { FloodGauge, PattaniRiverForecast, Shelter, FallbackTier } from "@yala/shared";
import { useT } from "../hooks/useLang";
import type { GaugeHistory } from "../hooks/useGaugeHistory";

/**
 * FloodWatchProfile — the "God's Mode" river profile for the FLOOD lens.
 *
 * Renders the entire Pattani River basin in one SVG:
 *   • Dam at the top (origin) with a horizontal storage bar
 *   • 5 gauges below as horizontal bars ordered upstream→downstream
 *   • Each bar: filled to current level, capped at warning threshold (dashed)
 *   • Status color drives the fill; pulsing warning gauges have a halo
 *   • Forecast strip at the bottom: current → 48h peak as a smoothed curve
 *
 * This is the centerpiece "see where the water is coming from, where it's
 * going, and what to do about it" view. The map shows the geography; this
 * chart shows the hydrology.
 */

interface Props {
  gauges: FloodGauge[];
  dam: { storagePct: number | null; outflowCms: number | null; status: string } | null;
  forecast: PattaniRiverForecast | null;
  shelters: Shelter[];
  history: GaugeHistory;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
}

const DAM_BAR_H = 18;
const GAUGE_BAR_H = 14;
const GAUGE_GAP = 16;
const FORECAST_H = 72;
const PADDING = 10;

/**
 * Severity → ramp token, plus the fill weight each level carries.
 *
 * Before this, the bars used `--good` for normal and `--warn` for BOTH watch
 * and warning — two severity levels sharing one colour, so an operator could
 * not tell "watch" from "warning" at a glance. The Wada-derived ramp gives
 * every level its own value, ordered by lightness inside the warm band, and
 * tested for colour-vision separation.
 *
 * The FILL slot is deliberate: these are marks (bars, halos, sparklines), not
 * label text. The level name is already rendered as text beside each bar, so
 * meaning never rests on hue alone.
 */
const STATUS_RAMP: Record<FloodGauge["status"], { mark: string; text: string; fill: number }> = {
  normal:  { mark: "var(--st-normal-mark)",  text: "var(--st-normal-text)",  fill: 0.5 },
  watch:   { mark: "var(--st-watch-mark)",   text: "var(--st-watch-text)",   fill: 0.62 },
  warning: { mark: "var(--st-warning-mark)", text: "var(--st-warning-text)", fill: 0.85 },
  flood:   { mark: "var(--st-flood-mark)",   text: "var(--st-flood-text)",   fill: 1.0 },
  unknown: { mark: "var(--st-unknown-mark)", text: "var(--st-unknown-text)", fill: 0.35 },
};

function fmt(n: number | null | undefined, digits = 1, unit = ""): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${n.toFixed(digits)}${unit}`;
}

function ellideName(name: string, max = 22): string {
  if (name.length <= max) return name;
  return name.slice(0, max - 1) + "…";
}

/** Order gauges geographically: north (upstream) to south (downstream). */
function orderByFlow(gauges: FloodGauge[]): FloodGauge[] {
  return [...gauges].sort((a, b) => b.lat - a.lat);
}

/** Synthesize a 48h forecast curve from a single current+peak datapoint.
 *  The API only returns (current, peak, day-until-peak). We use a simple
 *  two-segment shape: linear rise to peak, then linear decay — enough to
 *  communicate "the wave is coming" without overpromising on the model. */
function synthForecastSeries(
  current: number,
  peak: number,
  peakDay: number,
  points = 24,
): Array<{ t: number; v: number }> {
  const peakHours = Math.max(1, peakDay) * 24;
  const series: Array<{ t: number; v: number }> = [];
  for (let i = 0; i <= points; i++) {
    const t = (i / points) * 48; // hours
    let v: number;
    if (t <= peakHours) {
      const frac = t / peakHours;
      v = current + (peak - current) * frac;
    } else {
      const frac = (t - peakHours) / (48 - peakHours);
      v = peak - (peak - current) * 0.8 * frac;
    }
    series.push({ t, v });
  }
  return series;
}

export function FloodWatchProfile({
  gauges,
  dam,
  forecast,
  shelters,
  history,
  ageMinutes,
  fallbackTier,
}: Props) {
  const t = useT();
  const chartRef = useRef<HTMLDivElement>(null);
  const [viewWidth, setViewWidth] = useState(320);
  useEffect(() => {
    const element = chartRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width > 0) setViewWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const ordered = useMemo(() => orderByFlow(gauges), [gauges]);
  // The max level across all gauges sets the X-axis cap. We want to see
  // the warning threshold clearly, so the cap is max(2× warningM, 1.3× max level).
  const maxLevel = useMemo(() => {
    let m = 0;
    for (const g of ordered) {
      if (g.levelM != null) m = Math.max(m, g.levelM);
      if (g.warningM != null) m = Math.max(m, g.warningM);
    }
    if (m === 0) m = 30; // sensible default if we have no readings
    return m * 1.25;
  }, [ordered]);

  const warningGauges = ordered.filter(
    (g) => g.status === "warning" || g.status === "flood",
  );

  // Profile vertical layout: dam (top) + gauges (below)
  const damY = PADDING;
  const gaugesStartY = damY + DAM_BAR_H + GAUGE_GAP + 8;
  const gaugesTotalH = ordered.length * (GAUGE_BAR_H + 4) - 4;
  const forecastY = gaugesStartY + gaugesTotalH + GAUGE_GAP;
  const totalH = forecastY + FORECAST_H + 22; // 22 for footer

  // Forecast sparkline data
  const forecastSeries = useMemo(() => {
    if (!forecast || forecast.dischargeCms == null || forecast.forecastPeakCms == null) return [];
    return synthForecastSeries(
      forecast.dischargeCms,
      forecast.forecastPeakCms,
      forecast.forecastPeakDay ?? 2,
    );
  }, [forecast]);

  const forecastMax = useMemo(() => {
    if (forecastSeries.length === 0) return 0;
    return Math.max(...forecastSeries.map((p) => p.v)) * 1.2;
  }, [forecastSeries]);

  return (
    <div className="flood-watch" ref={chartRef}>
      <div className="flood-watch-head">
        <span className="eyebrow mono">
          {t("FLOOD WATCH · PATTANI RIVER", "เฝ้าระวังน้ำ · แม่น้ำปัตตานี")}
        </span>
        {fallbackTier && fallbackTier !== "live" && (
          <span
            className={`panel-tier-warn mono${fallbackTier === "unavailable" ? " panel-tier-warn--crit" : ""}`}
          >
            {fallbackTier.toUpperCase()}
          </span>
        )}
        {ageMinutes != null && (
          <span className="data-age mono">
            {fallbackTier === "unavailable" ? "OFFLINE" : t(`${ageMinutes}m ago`, `${ageMinutes} นาทีที่แล้ว`)}
          </span>
        )}
      </div>

      <svg
        viewBox={`0 0 ${viewWidth} ${totalH}`}
        width="100%"
        height={totalH}
        role="img"
        aria-label={t(
          "River profile from Bang Lang Dam downstream through the Pattani River gauges",
          "โปรไฟล์แม่น้ำจากเขื่อนบางลางผ่านสถานีวัดน้ำแม่น้ำปัตตานีลงสู่ทะเล",
        )}
      >
        {/* Vertical "river" spine: thin line connecting dam → gauges */}
        <line
          x1={PADDING + 6}
          y1={PADDING + DAM_BAR_H / 2}
          x2={PADDING + 6}
          y2={gaugesStartY + gaugesTotalH}
          stroke="var(--line-2)"
          strokeWidth={1.5}
          strokeDasharray="2 2"
        />

        {/* ── Bang Lang Dam (origin) ──────────────────────────────────────── */}
        <g transform={`translate(${PADDING}, ${damY})`}>
          <text x={0} y={10} className="flood-watch-label">
            {t("DAM · บางลาง", "เขื่อน · บางลาง")}
          </text>
          <text x={viewWidth - PADDING * 2} y={10} textAnchor="end" className="flood-watch-value">
            {dam?.storagePct != null ? `${dam.storagePct.toFixed(0)}%` : "—"}
          </text>
          <rect
            x={0}
            y={14}
            width={viewWidth - PADDING * 2}
            height={DAM_BAR_H - 14}
            fill="var(--line)"
            rx={0}
          />
          {dam?.storagePct != null && (
            <rect
              x={0}
              y={14}
              width={((viewWidth - PADDING * 2) * Math.max(0, Math.min(100, dam.storagePct))) / 100}
              height={DAM_BAR_H - 14}
              fill="var(--good)"
              opacity={0.85}
            />
          )}
        </g>

        {/* ── Gauges (upstream → downstream) ──────────────────────────────── */}
        {ordered.map((g, i) => {
          const y = gaugesStartY + i * (GAUGE_BAR_H + 4);
          const xScale = (v: number) => (v / maxLevel) * (viewWidth - PADDING * 2);
          const ramp = STATUS_RAMP[g.status] ?? STATUS_RAMP.unknown;
          const fillColor = ramp.mark;
          const fillOpacity = ramp.fill;
          const w = g.levelM != null ? xScale(g.levelM) : 0;
          const wW = g.warningM != null ? xScale(g.warningM) : 0;
          const isStressed = g.status === "warning" || g.status === "flood";

          // Sparkline polyline if we have history for this gauge
          const hist = history[g.id] ?? [];
          let sparkPath: string | null = null;
          if (hist.length >= 2) {
            const minL = Math.min(...hist.map((p) => p.levelM ?? Infinity));
            const maxL = Math.max(...hist.map((p) => p.levelM ?? -Infinity));
            const range = Math.max(0.5, maxL - minL);
            const w = viewWidth - PADDING * 2;
            sparkPath = hist
              .map((p, idx) => {
                if (p.levelM == null) return null;
                const px = (idx / (hist.length - 1)) * w;
                const py = ((maxL - p.levelM) / range) * 6; // 6px tall sparkline
                return `${idx === 0 ? "M" : "L"} ${px.toFixed(1)} ${py.toFixed(1)}`;
              })
              .filter(Boolean)
              .join(" ");
          }

          return (
            <g key={g.id} transform={`translate(${PADDING}, ${y})`}>
              {/* Halo for warning/flood gauges — concentric pulse, animated via CSS */}
              {isStressed && (
                <circle
                  cx={0}
                  cy={GAUGE_BAR_H / 2 + 2}
                  r={5}
                  fill="none"
                  stroke={fillColor}
                  strokeWidth={1}
                  opacity={0.4}
                  className="flood-watch-halo"
                />
              )}

              {/* Gauge name (truncate) */}
              <text x={0} y={10} className="flood-watch-label">
                {ellideName(g.name, 18)}
              </text>

              {/* Current level (m) */}
              <text
                x={viewWidth - PADDING * 2}
                y={10}
                textAnchor="end"
                className="flood-watch-value"
                style={{ fill: ramp.text }}
              >
                {fmt(g.levelM, 2, "m")}
              </text>

              {/* Bar background */}
              <rect
                x={0}
                y={13}
                width={viewWidth - PADDING * 2}
                height={GAUGE_BAR_H - 13}
                fill="var(--line)"
                opacity={0.5}
              />
              {/* Current level fill */}
              {w > 0 && (
                <rect
                  x={0}
                  y={13}
                  width={w}
                  height={GAUGE_BAR_H - 13}
                  fill={fillColor}
                  opacity={fillOpacity}
                />
              )}
              {/* Warning threshold line */}
              {wW > 0 && (
                <line
                  x1={wW}
                  y1={11}
                  x2={wW}
                  y2={GAUGE_BAR_H + 1}
                  stroke="var(--bad)"
                  strokeWidth={1.2}
                  strokeDasharray="2 2"
                  opacity={0.85}
                />
              )}
              {/* Sparkline (last 24h) on top of the bar */}
              {sparkPath && (
                <path
                  d={sparkPath}
                  fill="none"
                  stroke={fillColor}
                  strokeWidth={1}
                  opacity={0.95}
                  transform={`translate(0, ${GAUGE_BAR_H - 2})`}
                />
              )}
            </g>
          );
        })}

        {/* X-axis label */}
        <text x={viewWidth - PADDING * 2} y={gaugesStartY + gaugesTotalH + 12} textAnchor="end" className="flood-watch-axis">
          {t("level (m)", "ระดับน้ำ (ม.)")}
        </text>
        <text x={0} y={gaugesStartY + gaugesTotalH + 12} className="flood-watch-axis">
          {t("UPSTREAM", "ต้นน้ำ")} → {t("DOWNSTREAM", "ปลายน้ำ")}
        </text>

        {/* ── Forecast strip (48h) ─────────────────────────────────────────── */}
        {forecastSeries.length > 0 && (
          <g transform={`translate(${PADDING}, ${forecastY})`}>
            <text x={0} y={10} className="flood-watch-label">
              {t("48H FORECAST · DISCHARGE", "พยากรณ์ 48 ชม. · อัตราการไหล")}
            </text>
            <text
              x={viewWidth - PADDING * 2}
              y={24}
              textAnchor="end"
              className="flood-watch-value"
            >
              {fmt(forecast?.forecastPeakCms, 1, " m³/s")} {t("peak", "สูงสุด")}
            </text>
            {/* Forecast curve */}
            {(() => {
              const w = viewWidth - PADDING * 2;
              const path = forecastSeries
                .map((p, i) => {
                  const x = (p.t / 48) * w;
                  const y = 32 + (1 - p.v / forecastMax) * (FORECAST_H - 36);
                  return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
                })
                .join(" ");
              const areaPath = `${path} L ${w} ${FORECAST_H - 4} L 0 ${FORECAST_H - 4} Z`;
              return (
                <g>
                  <path d={areaPath} fill="var(--accent)" opacity={0.15} />
                  <path d={path} fill="none" stroke="var(--accent)" strokeWidth={1.5} />
                </g>
              );
            })()}
            {/* Day markers */}
            <line
              x1={0}
              y1={FORECAST_H - 4}
              x2={viewWidth - PADDING * 2}
              y2={FORECAST_H - 4}
              stroke="var(--line)"
              strokeWidth={0.5}
            />
            {[0, 24, 48].map((h) => {
              const w = viewWidth - PADDING * 2;
              return (
                <text
                  key={h}
                  x={(h / 48) * w}
                  y={FORECAST_H + 4}
                  className="flood-watch-axis"
                  textAnchor="middle"
                >
                  {h === 0 ? "now" : `+${h}h`}
                </text>
              );
            })}
          </g>
        )}

        {/* Footer / actions */}
        <text x={PADDING} y={totalH - 4} className="flood-watch-axis">
          {shelters.length > 0
            ? t(
                `${shelters.length} shelters · ${warningGauges.length} warnings`,
                `ศูนย์พักพิง ${shelters.length} · เตือนภัย ${warningGauges.length}`,
              )
            : t(
                `${warningGauges.length} gauges above warning`,
                `สถานี ${warningGauges.length} แห่งเกินเกณฑ์เตือนภัย`,
              )}
        </text>
      </svg>

      {/* Active-risk callout — visible only when something actually needs attention */}
      {warningGauges.length > 0 && (
        <div className="flood-watch-alert" role="alert">
          <div className="eyebrow mono" style={{ color: "var(--st-flood-text)" }}>
            {t("▲ ACTION REQUIRED", "▲ ต้องดำเนินการ")}
          </div>
          <div style={{ marginTop: 2 }}>
            {warningGauges.map((g) => (
              <div key={g.id} className="flood-watch-alert-row">
                <span className="mono">{g.name}</span>
                <span className="mono" style={{ color: "var(--st-flood-text)", marginLeft: 6 }}>
                  {g.status.toUpperCase()} · {fmt(g.levelM, 2, "m")}
                  {g.warningM != null ? ` / ${g.warningM.toFixed(1)}m` : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
